import React from 'react';
import { getAdminVehicles, setVehicleActive } from '../../services/admin';
import { activateVehicle, createVehicle, deactivateVehicle, getVehicles, updateVehicle } from '../../services/fleet';
import { Role } from '../../types/app';

const PAGE_SIZE = 50;

type VehicleRecord = {
  id: string;
  name: string;
  plateNumber: string;
  type: string;
  status: string;
  make: string;
  model: string;
  year: string;
  color: string;
  odometer: string;
  maintenanceDate: string;
  trackerDeviceId: string;
  capacity: string;
  metadata: Record<string, unknown>;
  owner: string;
  fleet: string;
  createdAt: string;
  updatedAt: string;
};

export function Vehicles({ role = '', searchQuery = '' }: { role?: Role | ''; searchQuery?: string }) {
  const isAdmin = role === 'admin' || role === 'super_admin';
  const [vehicles, setVehicles] = React.useState<VehicleRecord[]>([]);
  const [selectedVehicle, setSelectedVehicle] = React.useState<VehicleRecord | null>(null);
  const [deactivatingVehicle, setDeactivatingVehicle] = React.useState<VehicleRecord | null>(null);
  const [deactivationReason, setDeactivationReason] = React.useState('');
  const [deactivationNotes, setDeactivationNotes] = React.useState('');
  const [status, setStatus] = React.useState('');
  const [type, setType] = React.useState('');
  const [debouncedSearch, setDebouncedSearch] = React.useState(searchQuery);
  const [offset, setOffset] = React.useState(0);
  const [total, setTotal] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [activeVehicleId, setActiveVehicleId] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [showRegister, setShowRegister] = React.useState(false);
  const [registerPlate, setRegisterPlate] = React.useState('');
  const [registerMake, setRegisterMake] = React.useState('');
  const [registerModel, setRegisterModel] = React.useState('');
  const [registerCapacity, setRegisterCapacity] = React.useState('');
  const [registerType, setRegisterType] = React.useState('car');
  const [registerYear, setRegisterYear] = React.useState('');
  const [registerColor, setRegisterColor] = React.useState('');
  const [registerOdometer, setRegisterOdometer] = React.useState('');
  const [registerTrackerDeviceId, setRegisterTrackerDeviceId] = React.useState('');
  const [isRegistering, setIsRegistering] = React.useState(false);
  const [registerError, setRegisterError] = React.useState('');
  const [editingVehicle, setEditingVehicle] = React.useState<VehicleRecord | null>(null);
  const [editPlate, setEditPlate] = React.useState('');
  const [editMake, setEditMake] = React.useState('');
  const [editModel, setEditModel] = React.useState('');
  const [editCapacity, setEditCapacity] = React.useState('');
  const [editType, setEditType] = React.useState('car');
  const [editYear, setEditYear] = React.useState('');
  const [editColor, setEditColor] = React.useState('');
  const [editOdometer, setEditOdometer] = React.useState('');
  const [editTrackerDeviceId, setEditTrackerDeviceId] = React.useState('');
  const [isSavingEdit, setIsSavingEdit] = React.useState(false);
  const [editError, setEditError] = React.useState('');
  const [statusVehicle, setStatusVehicle] = React.useState<VehicleRecord | null>(null);
  const [isTogglingStatus, setIsTogglingStatus] = React.useState(false);
  const [statusToggleError, setStatusToggleError] = React.useState('');

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setOffset(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  React.useEffect(() => {
    let isActive = true;
    setIsLoading(true);
    setError('');

    const request = isAdmin
      ? getAdminVehicles({ search: debouncedSearch, status, type, limit: PAGE_SIZE, offset })
      : getVehicles({ status: status || undefined, type: type || undefined });

    request
      .then((response) => {
        if (isActive) {
          const records = normalizeVehicles(response);
          setVehicles(records);
          setTotal(readNestedNumber(response, ['total', 'count', 'vehicles_total', 'total_vehicles']) ?? records.length);
        }
      })
      .catch((requestError) => {
        if (isActive) {
          setVehicles([]);
          setError(requestError instanceof Error ? requestError.message : 'Unable to load vehicles');
        }
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [debouncedSearch, isAdmin, offset, status, type]);

  // Fleet manager: client-side filter by nav-bar search query
  const visibleVehicles = isAdmin ? vehicles : vehicles.filter((vehicle) => {
    if (!debouncedSearch.trim()) return true;
    const q = debouncedSearch.toLowerCase();
    return [vehicle.plateNumber, vehicle.make, vehicle.model, vehicle.name, vehicle.trackerDeviceId].some((field) => field.toLowerCase().includes(q));
  });

  React.useEffect(() => {
    if (!selectedVehicle && !deactivatingVehicle) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedVehicle(null);
        setDeactivatingVehicle(null);
      }
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [deactivatingVehicle, selectedVehicle]);

  const pageStart = total ? offset + 1 : 0;
  const pageEnd = Math.min(offset + vehicles.length, total);

  const updateVehicleStatus = async (
    vehicle: VehicleRecord,
    isActive: boolean,
    details?: { reason: string; notes: string },
  ) => {
    setActiveVehicleId(vehicle.id);
    setError('');
    setMessage('');

    try {
      await setVehicleActive(vehicle.id, isActive, details);
      const nextStatus = isActive ? 'active' : 'inactive';
      setVehicles((current) => current.map((item) => item.id === vehicle.id ? { ...item, status: nextStatus } : item));
      setSelectedVehicle((current) => current?.id === vehicle.id ? { ...current, status: nextStatus } : current);
      setDeactivatingVehicle(null);
      setDeactivationReason('');
      setDeactivationNotes('');
      setMessage(`${vehicle.name} was ${isActive ? 'activated' : 'deactivated'}.`);
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Unable to update vehicle status');
    } finally {
      setActiveVehicleId('');
    }
  };

  const openRegister = () => {
    setShowRegister(true);
    setRegisterPlate('');
    setRegisterMake('');
    setRegisterModel('');
    setRegisterCapacity('');
    setRegisterType('car');
    setRegisterYear('');
    setRegisterColor('');
    setRegisterOdometer('');
    setRegisterTrackerDeviceId('');
    setRegisterError('');
  };

  const submitRegister = async () => {
    const registration_number = registerPlate.trim();
    const make = registerMake.trim();
    const model = registerModel.trim();
    const seating_capacity = Number(registerCapacity);
    if (!registration_number) { setRegisterError('Enter the registration number'); return; }
    if (!make) { setRegisterError('Enter the make'); return; }
    if (!model) { setRegisterError('Enter the model'); return; }
    if (!registerCapacity || !Number.isFinite(seating_capacity) || seating_capacity < 1) {
      setRegisterError('Enter a valid seating capacity');
      return;
    }
    setIsRegistering(true);
    setRegisterError('');
    const year = registerYear.trim() ? Number(registerYear.trim()) : undefined;
    const odometer = registerOdometer.trim() ? Number(registerOdometer.trim()) : undefined;
    const trackerDeviceId = registerTrackerDeviceId.trim();
    try {
      await createVehicle({
        registration_number,
        make,
        model,
        seating_capacity,
        type: registerType,
        ...(year !== undefined && Number.isFinite(year) ? { year } : {}),
        ...(registerColor.trim() ? { color: registerColor.trim() } : {}),
        ...(odometer !== undefined && Number.isFinite(odometer) ? { odometer } : {}),
        tracker_device_id: trackerDeviceId || null,
      });
      setShowRegister(false);
      setMessage(`${registration_number} was registered.`);
      setOffset(0);
      const records = normalizeVehicles(await getVehicles());
      setVehicles(records);
      setTotal((current) => current || records.length);
    } catch (requestError) {
      setRegisterError(requestError instanceof Error ? requestError.message : 'Unable to register vehicle');
    } finally {
      setIsRegistering(false);
    }
  };

  const refreshVehicles = async () => {
    const records = normalizeVehicles(await getVehicles({ status: status || undefined, type: type || undefined }));
    setVehicles(records);
    setTotal(records.length);
  };

  const openEdit = (vehicle: VehicleRecord) => {
    setEditingVehicle(vehicle);
    setEditPlate(vehicle.plateNumber === 'Not provided' ? '' : vehicle.plateNumber);
    setEditMake(vehicle.make === 'Not provided' ? '' : vehicle.make);
    setEditModel(vehicle.model === 'Not provided' ? '' : vehicle.model);
    setEditCapacity(vehicle.capacity === 'Not provided' ? '' : vehicle.capacity);
    setEditType(vehicle.type || 'car');
    setEditYear(vehicle.year === 'Not provided' ? '' : vehicle.year);
    setEditColor(vehicle.color === 'Not provided' ? '' : vehicle.color);
    setEditOdometer(vehicle.odometer === 'Not provided' ? '' : vehicle.odometer);
    setEditTrackerDeviceId(vehicle.trackerDeviceId === 'Not provided' ? '' : vehicle.trackerDeviceId);
    setEditError('');
  };

  const submitEdit = async () => {
    if (!editingVehicle) return;
    const registration_number = editPlate.trim();
    const make = editMake.trim();
    const model = editModel.trim();
    const seating_capacity = Number(editCapacity);
    if (!registration_number) { setEditError('Enter the registration number'); return; }
    if (!make) { setEditError('Enter the make'); return; }
    if (!model) { setEditError('Enter the model'); return; }
    if (!editCapacity || !Number.isFinite(seating_capacity) || seating_capacity < 1) {
      setEditError('Enter a valid seating capacity');
      return;
    }
    const year = editYear.trim() ? Number(editYear.trim()) : undefined;
    const odometer = editOdometer.trim() ? Number(editOdometer.trim()) : undefined;
    const trackerDeviceId = editTrackerDeviceId.trim();
    setIsSavingEdit(true);
    setEditError('');
    try {
      await updateVehicle(editingVehicle.id, {
        registration_number,
        make,
        model,
        seating_capacity,
        type: editType,
        ...(year !== undefined && Number.isFinite(year) ? { year } : {}),
        ...(editColor.trim() ? { color: editColor.trim() } : {}),
        ...(odometer !== undefined && Number.isFinite(odometer) ? { odometer } : {}),
        tracker_device_id: trackerDeviceId || null,
      });
      setEditingVehicle(null);
      setMessage(`${registration_number} was updated.`);
      await refreshVehicles();
    } catch (requestError) {
      setEditError(requestError instanceof Error ? requestError.message : 'Unable to update vehicle');
    } finally {
      setIsSavingEdit(false);
    }
  };

  const submitStatusToggle = async () => {
    if (!statusVehicle) return;
    const activating = statusVehicle.status !== 'active';
    setIsTogglingStatus(true);
    setStatusToggleError('');
    try {
      if (activating) await activateVehicle(statusVehicle.id);
      else await deactivateVehicle(statusVehicle.id);
      setStatusVehicle(null);
      setSelectedVehicle(null);
      setMessage(`${statusVehicle.name} was ${activating ? 'activated' : 'deactivated'}.`);
      await refreshVehicles();
    } catch (requestError) {
      setStatusToggleError(requestError instanceof Error ? requestError.message : `Unable to ${activating ? 'activate' : 'deactivate'} vehicle`);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  return (
    <div className="vehicles-page">
      <div className="section-head drivers-head">
        <div>
          <h2>Vehicles</h2>
          <span>{total.toLocaleString()} vehicle{total === 1 ? '' : 's'}</span>
        </div>
        <div className="driver-filters">
          <label>
            <span className="sr-only">Filter vehicles by status</span>
            <select value={status} onChange={(event) => { setStatus(event.target.value); setOffset(0); }}>
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="idle">Idle</option>
              <option value="inactive">Inactive</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </label>
          <label>
            <span className="sr-only">Filter vehicles by type</span>
            <select value={type} onChange={(event) => { setType(event.target.value); setOffset(0); }}>
              <option value="">All types</option>
              <option value="bus">Bus</option>
              <option value="car">Car</option>
              <option value="van">Van</option>
              <option value="truck">Truck</option>
              <option value="motorcycle">Motorcycle</option>
            </select>
          </label>
          {!isAdmin ? (
            <button className="btn btn-primary" type="button" onClick={openRegister}><PlusIcon /> Register vehicle</button>
          ) : null}
        </div>
      </div>

      {error ? <div className="kyc-message passengers-error" role="alert">{error}</div> : null}
      {message ? <div className="kyc-message" role="status">{message}</div> : null}

      <div className="data-table drivers-table">
        <table>
          <thead>
            <tr>
              <th>Registration</th>
              <th>Make / Model</th>
              <th>Type</th>
              <th>Capacity</th>
              <th>GPS Source</th>
              {isAdmin ? <th>Owner</th> : null}
              <th>Status</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td className="kyc-table-state" colSpan={isAdmin ? 8 : 7}>Loading vehicles...</td></tr>
            ) : visibleVehicles.length ? visibleVehicles.map((vehicle) => (
              <tr key={vehicle.id}>
                <td data-label="Registration">
                  <div className="row-cell">
                    <span className="vehicle-row-icon"><VehicleIcon /></span>
                    <span className="kyc-document">{vehicle.plateNumber}</span>
                  </div>
                </td>
                <td data-label="Make / Model">
                  <strong>{vehicle.make !== 'Not provided' ? vehicle.make : '—'}</strong>
                  {' '}{vehicle.model !== 'Not provided' ? vehicle.model : ''}
                  {vehicle.year !== 'Not provided' ? <span className="muted text-sm"> · {vehicle.year}</span> : null}
                </td>
                <td data-label="Type"><span className="pill info">{vehicle.type}</span></td>
                <td data-label="Capacity">{vehicle.capacity !== 'Not provided' ? `${vehicle.capacity} seats` : '—'}</td>
                <td data-label="GPS Source"><GpsSourceBadge trackerDeviceId={vehicle.trackerDeviceId} /></td>
                {isAdmin ? <td data-label="Owner">{vehicle.owner}</td> : null}
                <td data-label="Status"><span className={`pill ${statusTone(vehicle.status)}`}>{vehicle.status}</span></td>
                <td>
                  <div className="passenger-row-actions">
                    <button aria-label={`View ${vehicle.name}`} className="btn-icon borderless passenger-view-btn" title="View vehicle" type="button" onClick={() => setSelectedVehicle(vehicle)}>
                      <EyeIcon />
                    </button>
                    {!isAdmin ? (
                      <>
                        <button aria-label={`Edit ${vehicle.name}`} className="btn-icon borderless" title="Edit vehicle" type="button" onClick={() => openEdit(vehicle)}>
                          <EditIcon />
                        </button>
                        <button aria-label={`${vehicle.status === 'active' ? 'Deactivate' : 'Activate'} ${vehicle.name}`} className="btn-icon borderless" title={vehicle.status === 'active' ? 'Deactivate vehicle' : 'Activate vehicle'} type="button" onClick={() => { setStatusVehicle(vehicle); setStatusToggleError(''); }}>
                          {vehicle.status === 'active' ? <PowerOffIcon /> : <PowerOnIcon />}
                        </button>
                      </>
                    ) : null}
                    {isAdmin ? (
                      <button
                        className={`btn btn-sm ${vehicle.status === 'active' ? 'passenger-deactivate-btn' : 'btn-outline'}`}
                        disabled={activeVehicleId === vehicle.id}
                        type="button"
                        onClick={() => vehicle.status === 'active' ? setDeactivatingVehicle(vehicle) : updateVehicleStatus(vehicle, true)}
                      >
                        {vehicle.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                    ) : null}
                  </div>
                </td>
              </tr>
            )) : (
              <tr><td className="kyc-table-state" colSpan={isAdmin ? 8 : 7}>{debouncedSearch ? 'No vehicles match your search.' : 'No vehicles found.'}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {isAdmin ? (
        <div className="passengers-pagination">
          <span>Showing {pageStart}-{pageEnd} of {total.toLocaleString()}</span>
          <div>
            <button className="btn" disabled={offset === 0 || isLoading} type="button" onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>Previous</button>
            <button className="btn" disabled={offset + PAGE_SIZE >= total || isLoading} type="button" onClick={() => setOffset(offset + PAGE_SIZE)}>Next</button>
          </div>
        </div>
      ) : null}

      {selectedVehicle ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setSelectedVehicle(null)}>
          <section aria-labelledby="vehicle-details-title" aria-modal="true" className="signup-dialog driver-details-dialog" role="dialog" onMouseDown={(event) => event.stopPropagation()}>
            <div className="signup-dialog-head">
              <div>
                <span className="vehicle-row-icon"><VehicleIcon /></span>
                <div><h2 id="vehicle-details-title">{selectedVehicle.name}</h2><span className={`pill ${statusTone(selectedVehicle.status)}`}>{selectedVehicle.status}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close vehicle details" onClick={() => setSelectedVehicle(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <dl className="kyc-details">
              <VehicleDetail label="Registration number" value={selectedVehicle.plateNumber} document />
              <VehicleDetail label="Type" value={selectedVehicle.type} />
              <VehicleDetail label="Make" value={selectedVehicle.make} />
              <VehicleDetail label="Model" value={selectedVehicle.model} />
              <VehicleDetail label="Year" value={selectedVehicle.year} />
              <VehicleDetail label="Color" value={selectedVehicle.color} />
              <VehicleDetail label="Odometer" value={selectedVehicle.odometer} />
              <VehicleDetail label="Maintenance date" value={selectedVehicle.maintenanceDate} />
              <VehicleDetail label="GPS source" value={hasTrackerDevice(selectedVehicle.trackerDeviceId) ? 'Tracker' : 'Driver app'} />
              <VehicleDetail label="Tracker device" value={selectedVehicle.trackerDeviceId} document={selectedVehicle.trackerDeviceId !== 'Not provided'} />
              <VehicleDetail label="Capacity" value={selectedVehicle.capacity} />
              <VehicleDetail label="Owner" value={selectedVehicle.owner} />
              <VehicleDetail label="Fleet" value={selectedVehicle.fleet} />
              <VehicleDetail label="Created" value={selectedVehicle.createdAt} />
              <VehicleDetail label="Updated" value={selectedVehicle.updatedAt} />
              <VehicleDetail label="Vehicle ID" value={selectedVehicle.id} document />
              {Object.keys(selectedVehicle.metadata).length ? (
                <div><dt>Metadata</dt><dd className="kyc-document">{JSON.stringify(selectedVehicle.metadata)}</dd></div>
              ) : null}
            </dl>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setSelectedVehicle(null)}>Close</button>
              {!isAdmin ? (
                <>
                  <button className={selectedVehicle.status === 'active' ? 'btn btn-danger' : 'btn btn-outline'} type="button" onClick={() => { setStatusVehicle(selectedVehicle); setStatusToggleError(''); }}>{selectedVehicle.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                  <button className="btn btn-primary" type="button" onClick={() => { openEdit(selectedVehicle); setSelectedVehicle(null); }}>Edit vehicle</button>
                </>
              ) : null}
              {isAdmin ? (
                <button
                  className={selectedVehicle.status === 'active' ? 'btn btn-danger' : 'btn btn-primary'}
                  disabled={activeVehicleId === selectedVehicle.id}
                  type="button"
                  onClick={() => selectedVehicle.status === 'active' ? setDeactivatingVehicle(selectedVehicle) : updateVehicleStatus(selectedVehicle, true)}
                >
                  {selectedVehicle.status === 'active' ? 'Deactivate vehicle' : 'Activate vehicle'}
                </button>
              ) : null}
            </div>
          </section>
        </div>
      ) : null}

      {deactivatingVehicle ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setDeactivatingVehicle(null)}>
          <form
            aria-labelledby="deactivate-vehicle-title"
            aria-modal="true"
            className="signup-dialog passenger-deactivate-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => {
              event.preventDefault();
              updateVehicleStatus(deactivatingVehicle, false, {
                reason: deactivationReason.trim(),
                notes: deactivationNotes.trim(),
              });
            }}
          >
            <div className="signup-dialog-head">
              <div>
                <span className="vehicle-row-icon"><VehicleIcon /></span>
                <div><h2 id="deactivate-vehicle-title">Deactivate vehicle</h2><span>{deactivatingVehicle.name}</span></div>
              </div>
              <button className="btn-icon borderless" type="button" aria-label="Close deactivation dialog" onClick={() => setDeactivatingVehicle(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            <div className="form-group">
              <label htmlFor="vehicle-deactivation-reason">Reason</label>
              <input autoFocus id="vehicle-deactivation-reason" placeholder="e.g. vehicle_maintenance" required value={deactivationReason} onChange={(event) => setDeactivationReason(event.target.value)} />
            </div>
            <div className="form-group">
              <label htmlFor="vehicle-deactivation-notes">Notes</label>
              <textarea id="vehicle-deactivation-notes" placeholder="Additional notes (optional)" value={deactivationNotes} onChange={(event) => setDeactivationNotes(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setDeactivatingVehicle(null)}>Cancel</button>
              <button className="btn btn-danger" disabled={!deactivationReason.trim() || activeVehicleId === deactivatingVehicle.id} type="submit">Deactivate vehicle</button>
            </div>
          </form>
        </div>
      ) : null}

      {showRegister ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setShowRegister(false)}>
          <form
            aria-labelledby="register-vehicle-title"
            aria-modal="true"
            className="signup-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => { event.preventDefault(); submitRegister(); }}
          >
            <div className="signup-dialog-head">
              <div><h2 id="register-vehicle-title">Register vehicle</h2></div>
              <button aria-label="Close register dialog" className="btn-icon borderless" type="button" onClick={() => setShowRegister(false)}><span aria-hidden="true">&times;</span></button>
            </div>
            {registerError ? <div className="kyc-message passengers-error" role="alert">{registerError}</div> : null}
            <div className="form-group">
              <label htmlFor="register-vehicle-plate">Registration number</label>
              <input autoFocus id="register-vehicle-plate" placeholder="e.g. LAG-234-AB" value={registerPlate} onChange={(event) => setRegisterPlate(event.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="register-vehicle-make">Make</label>
                <input id="register-vehicle-make" placeholder="e.g. Toyota" value={registerMake} onChange={(event) => setRegisterMake(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="register-vehicle-model">Model</label>
                <input id="register-vehicle-model" placeholder="e.g. Hiace" value={registerModel} onChange={(event) => setRegisterModel(event.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="register-vehicle-capacity">Seating capacity</label>
                <input id="register-vehicle-capacity" inputMode="numeric" placeholder="e.g. 14" value={registerCapacity} onChange={(event) => setRegisterCapacity(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="register-vehicle-type">Type</label>
                <select id="register-vehicle-type" value={registerType} onChange={(event) => setRegisterType(event.target.value)}>
                  <option value="bus">Bus</option>
                  <option value="car">Car</option>
                  <option value="van">Van</option>
                  <option value="truck">Truck</option>
                  <option value="motorcycle">Motorcycle</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="register-vehicle-year">Year <span className="optional-label">Optional</span></label>
                <input id="register-vehicle-year" inputMode="numeric" placeholder="e.g. 2021" value={registerYear} onChange={(event) => setRegisterYear(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="register-vehicle-color">Color <span className="optional-label">Optional</span></label>
                <input id="register-vehicle-color" placeholder="e.g. White" value={registerColor} onChange={(event) => setRegisterColor(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="register-vehicle-odometer">Odometer (km) <span className="optional-label">Optional</span></label>
                <input id="register-vehicle-odometer" inputMode="numeric" placeholder="e.g. 45000" value={registerOdometer} onChange={(event) => setRegisterOdometer(event.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="register-vehicle-tracker">Tracker device ID <span className="optional-label">Optional</span></label>
              <input id="register-vehicle-tracker" placeholder="e.g. TRK-LAG-234-AB" value={registerTrackerDeviceId} onChange={(event) => setRegisterTrackerDeviceId(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setShowRegister(false)}>Cancel</button>
              <button className="btn btn-primary" disabled={isRegistering} type="submit">{isRegistering ? 'Registering...' : 'Register vehicle'}</button>
            </div>
          </form>
        </div>
      ) : null}

      {editingVehicle ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setEditingVehicle(null)}>
          <form
            aria-labelledby="edit-vehicle-title"
            aria-modal="true"
            className="signup-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
            onSubmit={(event) => { event.preventDefault(); submitEdit(); }}
          >
            <div className="signup-dialog-head">
              <div><h2 id="edit-vehicle-title">Edit vehicle</h2></div>
              <button aria-label="Close edit dialog" className="btn-icon borderless" type="button" onClick={() => setEditingVehicle(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            {editError ? <div className="kyc-message passengers-error" role="alert">{editError}</div> : null}
            <div className="form-group">
              <label htmlFor="edit-vehicle-plate">Registration number</label>
              <input autoFocus id="edit-vehicle-plate" placeholder="e.g. LAG-234-AB" value={editPlate} onChange={(event) => setEditPlate(event.target.value)} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-vehicle-make">Make</label>
                <input id="edit-vehicle-make" placeholder="e.g. Toyota" value={editMake} onChange={(event) => setEditMake(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="edit-vehicle-model">Model</label>
                <input id="edit-vehicle-model" placeholder="e.g. Hiace" value={editModel} onChange={(event) => setEditModel(event.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-vehicle-capacity">Seating capacity</label>
                <input id="edit-vehicle-capacity" inputMode="numeric" placeholder="e.g. 14" value={editCapacity} onChange={(event) => setEditCapacity(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="edit-vehicle-type">Type</label>
                <select id="edit-vehicle-type" value={editType} onChange={(event) => setEditType(event.target.value)}>
                  <option value="bus">Bus</option>
                  <option value="car">Car</option>
                  <option value="van">Van</option>
                  <option value="truck">Truck</option>
                  <option value="motorcycle">Motorcycle</option>
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="edit-vehicle-year">Year <span className="optional-label">Optional</span></label>
                <input id="edit-vehicle-year" inputMode="numeric" placeholder="e.g. 2021" value={editYear} onChange={(event) => setEditYear(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="edit-vehicle-color">Color <span className="optional-label">Optional</span></label>
                <input id="edit-vehicle-color" placeholder="e.g. White" value={editColor} onChange={(event) => setEditColor(event.target.value)} />
              </div>
              <div className="form-group">
                <label htmlFor="edit-vehicle-odometer">Odometer (km) <span className="optional-label">Optional</span></label>
                <input id="edit-vehicle-odometer" inputMode="numeric" placeholder="e.g. 45000" value={editOdometer} onChange={(event) => setEditOdometer(event.target.value)} />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="edit-vehicle-tracker">Tracker device ID <span className="optional-label">Optional</span></label>
              <input id="edit-vehicle-tracker" placeholder="e.g. TRK-LAG-234-AB" value={editTrackerDeviceId} onChange={(event) => setEditTrackerDeviceId(event.target.value)} />
            </div>
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setEditingVehicle(null)}>Cancel</button>
              <button className="btn btn-primary" disabled={isSavingEdit} type="submit">{isSavingEdit ? 'Saving...' : 'Save changes'}</button>
            </div>
          </form>
        </div>
      ) : null}

      {statusVehicle ? (
        <div className="modal-backdrop" role="presentation" onMouseDown={() => setStatusVehicle(null)}>
          <div
            aria-labelledby="status-vehicle-title"
            aria-modal="true"
            className="signup-dialog passenger-deactivate-dialog"
            role="dialog"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <div className="signup-dialog-head">
              <div><h2 id="status-vehicle-title">{statusVehicle.status === 'active' ? 'Deactivate vehicle' : 'Activate vehicle'}</h2><span>{statusVehicle.name}</span></div>
              <button className="btn-icon borderless" type="button" aria-label="Close dialog" onClick={() => setStatusVehicle(null)}><span aria-hidden="true">&times;</span></button>
            </div>
            {statusToggleError ? <div className="kyc-message passengers-error" role="alert">{statusToggleError}</div> : null}
            {statusVehicle.status === 'active' ? (
              <p className="confirm-copy">Are you sure you want to deactivate <strong>{statusVehicle.name}</strong> ({statusVehicle.plateNumber})? The record is kept but marked inactive, and can be reactivated later.</p>
            ) : (
              <p className="confirm-copy">Activate <strong>{statusVehicle.name}</strong> ({statusVehicle.plateNumber}) so it can be assigned to routes again?</p>
            )}
            <div className="kyc-dialog-actions">
              <button className="btn" type="button" onClick={() => setStatusVehicle(null)}>Cancel</button>
              <button className={statusVehicle.status === 'active' ? 'btn btn-danger' : 'btn btn-primary'} disabled={isTogglingStatus} type="button" onClick={() => void submitStatusToggle()}>
                {isTogglingStatus ? (statusVehicle.status === 'active' ? 'Deactivating...' : 'Activating...') : (statusVehicle.status === 'active' ? 'Deactivate vehicle' : 'Activate vehicle')}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function normalizeVehicles(response: unknown): VehicleRecord[] {
  return getVehicleSource(response).map((vehicle, index) => {
    const owner = readObject(vehicle, 'owner') ?? readObject(vehicle, 'user') ?? {};
    const fleet = readObject(vehicle, 'fleet') ?? readObject(vehicle, 'sub_fleet') ?? {};
    const make = readString(vehicle, 'make', 'manufacturer', 'brand') ?? 'Not provided';
    const model = readString(vehicle, 'model') ?? 'Not provided';
    const plateNumber = readString(vehicle, 'registration_number', 'plate_number', 'plateNumber') ?? 'Not provided';
    const generatedName = [make, model].filter((value) => value !== 'Not provided').join(' ');
    const name = readString(vehicle, 'name', 'vehicle_name') ?? (generatedName || plateNumber);
    return {
      id: readString(vehicle, 'id', 'vehicle_id') ?? String(index),
      name,
      plateNumber,
      type: (readString(vehicle, 'type', 'vehicle_type', 'category') ?? 'vehicle').replace(/_/g, ' '),
      status: (readString(vehicle, 'status') ?? (readBoolean(vehicle, 'is_active', 'active') === false ? 'inactive' : 'active')).toLowerCase(),
      make,
      model,
      year: readValue(vehicle, 'year', 'manufacture_year') ?? 'Not provided',
      color: readString(vehicle, 'color') ?? 'Not provided',
      odometer: readValue(vehicle, 'odometer') ?? 'Not provided',
      maintenanceDate: formatDate(readString(vehicle, 'maintenance_date')),
      trackerDeviceId: readString(vehicle, 'tracker_device_id') ?? 'Not provided',
      metadata: readObject(vehicle, 'metadata') ?? {},
      capacity: readValue(vehicle, 'capacity', 'seating_capacity', 'passenger_capacity') ?? 'Not provided',
      owner: readString(vehicle, 'owner_name') ?? readString(owner, 'full_name', 'name', 'email') ?? 'Not provided',
      fleet: readString(vehicle, 'fleet_name', 'sub_fleet_name') ?? readString(fleet, 'name', 'fleet_name') ?? 'Not assigned',
      createdAt: formatDate(readString(vehicle, 'created_at', 'registered_at', 'added_at')),
      updatedAt: formatDate(readString(vehicle, 'updated_at')),
    };
  });
}

function getVehicleSource(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response.filter(isObject);
  if (!isObject(response)) return [];
  for (const key of ['vehicles', 'items', 'results', 'records', 'data']) {
    const value = response[key];
    if (Array.isArray(value)) return value.filter(isObject);
    if (isObject(value)) {
      const vehicles = getVehicleSource(value);
      if (vehicles.length) return vehicles;
    }
  }
  return [];
}

function VehicleDetail({ document = false, label, value }: { document?: boolean; label: string; value: string }) {
  return <div><dt>{label}</dt><dd className={document ? 'kyc-document' : undefined}>{value}</dd></div>;
}

function GpsSourceBadge({ trackerDeviceId }: { trackerDeviceId: string }) {
  if (hasTrackerDevice(trackerDeviceId)) {
    return <span className="pill success" title={`Tracker device: ${trackerDeviceId}`}>Tracker</span>;
  }

  return <span className="pill muted" title="Position source: driver's phone">Driver app</span>;
}

function hasTrackerDevice(trackerDeviceId: string) {
  return Boolean(trackerDeviceId && trackerDeviceId !== 'Not provided');
}

function VehicleIcon() {
  return <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 17h14l-1-7a2 2 0 00-2-2H8a2 2 0 00-2 2l-1 7zM3 14h18" /><circle cx="7" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></svg>;
}

function PlusIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14" /></svg>;
}

function EditIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 20h4l10-10-4-4L4 16v4z" /><path d="M13 7l4 4" /></svg>;
}

function PowerOffIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v8" /><path d="M18.4 6.6a8 8 0 11-12.8 0" /></svg>;
}

function PowerOnIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
}

function EyeIcon() {
  return <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6z" /><circle cx="12" cy="12" r="3" /></svg>;
}

function statusTone(status: string) {
  if (status === 'active') return 'success';
  if (status === 'inactive') return 'danger';
  return 'warn';
}

function formatDate(value: string | undefined) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function readString(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

function readValue(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'string' || typeof value === 'number') return String(value);
  }
  return undefined;
}

function readBoolean(source: Record<string, unknown>, ...keys: string[]) {
  for (const key of keys) {
    const value = source[key];
    if (typeof value === 'boolean') return value;
  }
  return undefined;
}

function readObject(source: Record<string, unknown>, key: string) {
  return isObject(source[key]) ? source[key] as Record<string, unknown> : undefined;
}

function readNestedNumber(response: unknown, keys: string[]): number | undefined {
  if (!isObject(response)) return undefined;
  for (const key of keys) {
    const value = response[key];
    if (typeof value === 'number') return value;
  }
  for (const value of Object.values(response)) {
    const nested = readNestedNumber(value, keys);
    if (nested !== undefined) return nested;
  }
  return undefined;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
